import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  CircularProgress,
  Button,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { EditNoteIcon } from "../../components/Icons";
import { Layout } from "../../components/layout";
import { TextInput } from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import { DateInput } from "../../components/DateInput";
import { Select } from "../../components/Select";
import {
  usersService,
  UserDetails,
  Specialty,
} from "../../services/usersService";
import { UserRole } from "../../constants";
import { authService } from "../../services/authService";
import {
  validateUSPhoneNumber,
  cleanPhoneNumber,
} from "../../utils/phoneValidation";
import { validateEmail } from "../../utils/emailValidation";
import { selectUser } from "../../store/selectors/authSelectors";
import { setUser } from "../../store/slices/authSlice";
import { colors } from "../../theme/colors";
import { useNavigationBlocker } from "../../hooks";
import { useNavigationBlockerContext } from "../../contexts";
import {
  PageContainer,
  EditContainer,
  AvatarUploadSection,
  AvatarEditContainer,
  AvatarEditOverlay,
  AvatarContainer,
  AvatarPlaceholder,
  Avatar,
  UploadText,
  FormSection,
  FormColumn,
  FormSectionTitle,
  FormRow,
  FooterActions,
  NoDataText,
  PageHeader,
} from "./UserProfilePage.styled";

interface FormData {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  dentalLicenseNumber: string;
  specialtyLicenseNumber: string;
  deaNumber: string;
  npiNumber: string;
  specialtyIds: number[];
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  dentalLicenseNumber?: string;
  specialtyLicenseNumber?: string;
  deaNumber?: string;
  npiNumber?: string;
}

interface EditUserProfilePageProps {
  isCurrentUser?: boolean;
}

const EditUserProfilePage: React.FC<EditUserProfilePageProps> = ({
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const currentUser = useSelector(selectUser);

  const [user, setUserState] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(!isCurrentUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const fetchInitiatedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    displayName: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    dentalLicenseNumber: "",
    specialtyLicenseNumber: "",
    deaNumber: "",
    npiNumber: "",
    specialtyIds: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Fetch specialties from API
  useEffect(() => {
    usersService.getSpecialties().then(setSpecialties).catch(console.error);
  }, []);

  // For current user, use data from Redux
  useEffect(() => {
    if (isCurrentUser && currentUser) {
      const userData = currentUser as unknown as UserDetails;
      setUserState(userData);
      setFormData({
        displayName: userData.displayName || "",
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        phoneNumber: userData.phoneNumber || "",
        dateOfBirth: userData.dateOfBirth
          ? formatDateForInput(userData.dateOfBirth)
          : "",
        dentalLicenseNumber: userData.dentalLicenseNumber || "",
        specialtyLicenseNumber: userData.specialtyLicenseNumber || "",
        deaNumber: userData.deaNumber || "",
        npiNumber: userData.npiNumber || "",
        specialtyIds: userData.specialtyIds || [],
      });
      setLoading(false);
    }
  }, [isCurrentUser, currentUser]);

  // For other users, fetch by ID
  useEffect(() => {
    if (isCurrentUser || !id || fetchInitiatedRef.current) return;

    fetchInitiatedRef.current = true;
    setLoading(true);

    usersService
      .getUserById(parseInt(id))
      .then((data) => {
        setUserState(data);
        setFormData({
          displayName: data.displayName || "",
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phoneNumber: data.phoneNumber || "",
          dateOfBirth: data.dateOfBirth
            ? formatDateForInput(data.dateOfBirth)
            : "",
          dentalLicenseNumber: data.dentalLicenseNumber || "",
          specialtyLicenseNumber: data.specialtyLicenseNumber || "",
          deaNumber: data.deaNumber || "",
          npiNumber: data.npiNumber || "",
          specialtyIds: data.specialtyIds || [],
        });
      })
      .catch((err) => {
        console.error("Error fetching user:", err);
        setError(t("userProfile.loadError"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t, isCurrentUser]);

  const formatDateForInput = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getEditPageTitle = (roleId: number): string => {
    if (isCurrentUser) {
      return t("myProfile.editTitle");
    }
    switch (roleId) {
      case UserRole.Provider:
        return t("userProfile.editProviderProfile");
      case UserRole.Assistant:
        return t("userProfile.editAssistantProfile");
      case UserRole.Manager:
        return t("userProfile.editManagerProfile");
      default:
        return t("userProfile.editProviderProfile");
    }
  };

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const hasChanges = useMemo(() => {
    if (!user) return false;
    return (
      avatarPreview !== null ||
      formData.displayName !== (user.displayName || "") ||
      formData.firstName !== (user.firstName || "") ||
      formData.lastName !== (user.lastName || "") ||
      formData.email !== (user.email || "") ||
      cleanPhoneNumber(formData.phoneNumber) !== cleanPhoneNumber(user.phoneNumber || "") ||
      formData.dateOfBirth !==
        (user.dateOfBirth ? formatDateForInput(user.dateOfBirth) : "") ||
      formData.dentalLicenseNumber !== (user.dentalLicenseNumber || "") ||
      formData.specialtyLicenseNumber !== (user.specialtyLicenseNumber || "") ||
      formData.deaNumber !== (user.deaNumber || "") ||
      formData.npiNumber !== (user.npiNumber || "") ||
      JSON.stringify(formData.specialtyIds) !==
        JSON.stringify(user.specialtyIds || [])
    );
  }, [formData, user, avatarPreview]);

  const { confirmNavigation } = useNavigationBlocker({
    shouldBlock: hasChanges,
  });

  const { setConfirmNavigation } = useNavigationBlockerContext();

  useEffect(() => {
    setConfirmNavigation(confirmNavigation);
    return () => {
      setConfirmNavigation(null);
    };
  }, [confirmNavigation, setConfirmNavigation]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    const nameRegex = /^[a-zA-Z\-']+$/;

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("userProfile.validation.firstNameRequired");
    } else if (!nameRegex.test(formData.firstName)) {
      newErrors.firstName = t("userProfile.validation.firstNameInvalid");
    } else if (formData.firstName.length > 50) {
      newErrors.firstName = t("userProfile.validation.firstNameTooLong");
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t("userProfile.validation.lastNameRequired");
    } else if (!nameRegex.test(formData.lastName)) {
      newErrors.lastName = t("userProfile.validation.lastNameInvalid");
    } else if (formData.lastName.length > 50) {
      newErrors.lastName = t("userProfile.validation.lastNameTooLong");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("userProfile.validation.emailRequired");
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t("userProfile.validation.emailInvalid");
    }

    // Phone number validation (required)
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = t("userProfile.validation.phoneRequired");
    } else if (!validateUSPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = t("userProfile.validation.phoneInvalid");
    }

    // Dental License Number: letters, numbers, hyphens, max 10
    if (
      formData.dentalLicenseNumber &&
      !/^[a-zA-Z0-9-]{1,10}$/.test(formData.dentalLicenseNumber)
    ) {
      newErrors.dentalLicenseNumber = t(
        "userProfile.validation.invalidLicense",
        "Maximum 10 characters (letters, numbers, hyphens)",
      );
    }

    // Specialty License Number: letters, numbers, hyphens, max 10
    if (
      formData.specialtyLicenseNumber &&
      !/^[a-zA-Z0-9-]{1,10}$/.test(formData.specialtyLicenseNumber)
    ) {
      newErrors.specialtyLicenseNumber = t(
        "userProfile.validation.invalidLicense",
        "Maximum 10 characters (letters, numbers, hyphens)",
      );
    }

    // DEA Number: exactly 2 letters + 7 numbers (e.g. AB1234567)
    if (formData.deaNumber && !/^[a-zA-Z]{2}\d{7}$/.test(formData.deaNumber)) {
      newErrors.deaNumber = t(
        "userProfile.validation.invalidDEA",
        "Must be 2 letters followed by 7 numbers (e.g. AB1234567)",
      );
    }

    // NPI Number: numbers only, max 10
    if (formData.npiNumber && !/^\d{1,10}$/.test(formData.npiNumber)) {
      newErrors.npiNumber = t(
        "userProfile.validation.invalidNPI",
        "Numbers only, maximum 10 digits",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value;

      if (field === "npiNumber") {
        // Numbers only, max 10
        value = value.replace(/\D/g, "").slice(0, 10);
      } else if (field === "deaNumber") {
        // Letters, numbers, hyphens, max 9
        value = value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 9);
      } else if (
        field === "dentalLicenseNumber" ||
        field === "specialtyLicenseNumber"
      ) {
        // Letters, numbers, hyphens, max 10
        value = value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 10);
      }
      
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSpecialtyChange = (value: unknown) => {
    setFormData((prev) => ({ ...prev, specialtyIds: value as number[] }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !user) return;
    if (!isCurrentUser && !id) return;

    setSaving(true);
    try {
      if (isCurrentUser) {
        // Use /me endpoints for current user
        if (avatarFile) {
          await authService.uploadCurrentUserAvatar(avatarFile);
        }

        const updatedUser = await authService.updateCurrentUser({
          displayName: formData.displayName,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber
            ? cleanPhoneNumber(formData.phoneNumber)
            : undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          dentalLicenseNumber: formData.dentalLicenseNumber || undefined,
          specialtyLicenseNumber: formData.specialtyLicenseNumber || undefined,
          deaNumber: formData.deaNumber || undefined,
          npiNumber: formData.npiNumber || undefined,
          specialtyIds: formData.specialtyIds,
        });

        // Update Redux store with new user data — the useEffect([currentUser])
        // will re-sync formData, making hasChanges false automatically
        dispatch(setUser(updatedUser));
        // Clear avatar state so avatarPreview !== null doesn't keep hasChanges true
        setAvatarFile(null);
        setAvatarPreview(null);
        setShowSuccessSnackbar(true);
      } else {
        // Use /users/:id endpoints for other users
        if (avatarFile) {
          await usersService.uploadAvatar(parseInt(id!), avatarFile);
        }

        await usersService.updateUser({
          id: parseInt(id!),
          email: formData.email,
          roleId: user.roleId,
          status: user.status,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber
            ? cleanPhoneNumber(formData.phoneNumber)
            : undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          dentalLicenseNumber: formData.dentalLicenseNumber || undefined,
          specialtyLicenseNumber: formData.specialtyLicenseNumber || undefined,
          deaNumber: formData.deaNumber || undefined,
          npiNumber: formData.npiNumber || undefined,
          specialtyIds: formData.specialtyIds,
        });
        // Sync the user baseline with saved values so hasChanges becomes false
        setUserState((prev) =>
          prev
            ? {
                ...prev,
                displayName: formData.displayName,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                phoneNumber: formData.phoneNumber,
                dateOfBirth: formData.dateOfBirth || prev.dateOfBirth,
          dentalLicenseNumber: formData.dentalLicenseNumber,
          specialtyLicenseNumber: formData.specialtyLicenseNumber,
          deaNumber: formData.deaNumber,
          npiNumber: formData.npiNumber,
          specialtyIds: formData.specialtyIds,
              }
            : prev,
        );
        setAvatarFile(null);
        setAvatarPreview(null);
        setShowSuccessSnackbar(true);
      }
    } catch (err) {
      console.error("Error updating user:", err);
      
      const axiosError = err as {
        response?: { 
          status?: number; 
          data?: { 
            detail?: string;
            errors?: Record<string, string[]>;
          };
        };
      };

      // Handle validation errors with specific field errors from errors object
      if (axiosError.response?.data?.errors) {
        const apiErrors = axiosError.response.data.errors;
        const newErrors: FormErrors = {};
        
        // Map API field names to form field names and extract all error messages
        const fieldMapping: Record<string, keyof FormErrors> = {
          Email: "email",
          PhoneNumber: "phoneNumber",
          FirstName: "firstName",
          LastName: "lastName",
          DateOfBirth: "dateOfBirth",
          DentalLicenseNumber: "dentalLicenseNumber",
          SpecialtyLicenseNumber: "specialtyLicenseNumber",
          DeaNumber: "deaNumber",
          NpiNumber: "npiNumber",
        };

        // Process all error fields
        Object.keys(apiErrors).forEach((apiFieldName) => {
          const formFieldName = fieldMapping[apiFieldName];
          if (formFieldName && apiErrors[apiFieldName].length > 0) {
            // Join all error messages for this field
            newErrors[formFieldName] = apiErrors[apiFieldName].join(" ");
          }
        });

        if (Object.keys(newErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...newErrors }));
        } else {
          // If no mapped fields found, show generic error
          setError(
            axiosError.response.data.detail || t("userProfile.updateError"),
          );
        }
      } else {
        // Fallback to generic error message
        setError(
          axiosError.response?.data?.detail || t("userProfile.updateError"),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    confirmNavigation(() => {
      if (isCurrentUser) {
        navigate("/profile", { replace: true });
      } else {
        navigate(`/user-management/${id}`, { replace: true });
      }
    });
  };

  const specialtyOptions = specialties.map((spec) => ({
    value: spec.id,
    label: spec.name,
  }));

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      </Layout>
    );
  }

  if (error || !user) {
    return (
      <Layout>
        <PageContainer>
          <NoDataText>{error || t("userProfile.notFound")}</NoDataText>
        </PageContainer>
      </Layout>
    );
  }

  const isProvider = user.roleId === UserRole.Provider;

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <Typography variant="h1">{getEditPageTitle(user.roleId)}</Typography>
        </PageHeader>

        <EditContainer>
          <AvatarUploadSection
            onClick={handleAvatarClick}
            sx={{ cursor: "pointer" }}
          >
            <Box
              component="input"
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              sx={{ display: "none" }}
            />
            <AvatarEditContainer>
              <AvatarContainer>
                {avatarPreview ? (
                  <Avatar src={avatarPreview} alt={user.displayName || ""} />
                ) : user?.avatarUrl ? (
                  <Avatar src={user.avatarUrl} alt={user.displayName || ""} />
                ) : (
                  <AvatarPlaceholder>
                    {getInitials(formData.firstName, formData.lastName)}
                  </AvatarPlaceholder>
                )}
              </AvatarContainer>
              <AvatarEditOverlay>
                <EditNoteIcon width={22} height={22} />
              </AvatarEditOverlay>
            </AvatarEditContainer>
            <UploadText>{t("userProfile.uploadAvatar")}</UploadText>
          </AvatarUploadSection>

          <FormSection>
            <FormColumn>
              <FormSectionTitle>
                {t("userProfile.personalInformation")}
              </FormSectionTitle>
              {isCurrentUser && (
                <FormRow>
                  <TextInput
                    name="displayName"
                    label={t("userProfile.greetingsName")}
                    placeholder={t("userProfile.greetingsNamePlaceholder")}
                    value={formData.displayName}
                    onChange={handleInputChange("displayName")}
                    maxLength={50}
                  />
                </FormRow>
              )}
              <FormRow>
                <TextInput
                  name="firstName"
                  label={t("userProfile.firstName")}
                  placeholder={t("userProfile.firstNamePlaceholder")}
                  value={formData.firstName}
                  onChange={handleInputChange("firstName")}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                  maxLength={50}
                />
              </FormRow>
              <FormRow>
                <TextInput
                  name="lastName"
                  label={t("userProfile.lastName")}
                  placeholder={t("userProfile.lastNamePlaceholder")}
                  value={formData.lastName}
                  onChange={handleInputChange("lastName")}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                  maxLength={50}
                />
              </FormRow>
              <FormRow>
                <TextInput
                  name="email"
                  label={t("userProfile.email")}
                  placeholder={t("userProfile.emailPlaceholder")}
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
              </FormRow>
              <FormRow>
                <PhoneInput
                  name="phoneNumber"
                  label={t("userProfile.phoneNumber")}
                  placeholder={t("userProfile.phoneNumberPlaceholder")}
                  value={formData.phoneNumber}
                  onChange={handleInputChange("phoneNumber")}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  required
                />
              </FormRow>
              <FormRow>
                <DateInput
                  name="dateOfBirth"
                  label={t("userProfile.dateOfBirth")}
                  value={formData.dateOfBirth}
                  onChange={handleInputChange("dateOfBirth")}
                  required
                />
              </FormRow>
            </FormColumn>

            <FormColumn>
              <FormSectionTitle>
                {t("userProfile.professionalCredentials")}
              </FormSectionTitle>
              <FormRow>
                <TextInput
                  name="dentalLicenseNumber"
                  label={t("userProfile.dentalLicenseNumber")}
                  value={formData.dentalLicenseNumber}
                  onChange={handleInputChange("dentalLicenseNumber")}
                  error={!!errors.dentalLicenseNumber}
                  helperText={errors.dentalLicenseNumber}
                  maxLength={10}
                />
              </FormRow>
                <FormRow>
                  <TextInput
                    name="specialtyLicenseNumber"
                    label={t("userProfile.specialtyLicenseNumber")}
                    value={formData.specialtyLicenseNumber}
                    onChange={handleInputChange("specialtyLicenseNumber")}
                  error={!!errors.specialtyLicenseNumber}
                  helperText={errors.specialtyLicenseNumber}
                    maxLength={10}
                  />
                </FormRow>
              <FormRow>
                <TextInput
                  name="deaNumber"
                  label={t("userProfile.deaNumber")}
                  value={formData.deaNumber}
                  onChange={handleInputChange("deaNumber")}
                  error={!!errors.deaNumber}
                  helperText={errors.deaNumber}
                  maxLength={9}
                />
              </FormRow>
              <FormRow>
                <TextInput
                  name="npiNumber"
                  label={t("userProfile.npiNumber")}
                  value={formData.npiNumber}
                  onChange={handleInputChange("npiNumber")}
                  error={!!errors.npiNumber}
                  helperText={errors.npiNumber}
                  maxLength={10}
                />
              </FormRow>
              {isProvider && (
                <FormRow>
                  <Select
                    name="specialty"
                    label={t("userProfile.specialty")}
                    placeholder={t("userProfile.selectSpecialty")}
                    value={formData.specialtyIds}
                    onChange={handleSpecialtyChange}
                    options={specialtyOptions}
                    multiple
                  />
                </FormRow>
              )}
            </FormColumn>
          </FormSection>

          <FooterActions>
            <Button variant="outlined" onClick={handleCancel}>
              {t("userProfile.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              sx={{
                backgroundColor: colors.amber.main,
                "&:hover": { backgroundColor: colors.amber.dark },
              }}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : (
                t("userProfile.saveChanges")
              )}
            </Button>
          </FooterActions>
        </EditContainer>
      </PageContainer>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("userProfile.updateSuccess")}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default EditUserProfilePage;
